/**
 * Agent-built apps (slice 6i §5.3, §7): the apps a person can see, one app's
 * menu (versions, errors), and the owner's sharing, versions and delete.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../app/api";

export type AppSharing = "owner" | "people" | "crew";

export type AppItem = {
  id: string;
  title: string;
  description: string;
  version: number;
  versions: number;
  owner: { id: string; name: string };
  /** Whether it's the signed-in person's own: sharing, versions and delete are. */
  mine: boolean;
  sharing: AppSharing;
  /** Who it's shared with, when `sharing` is `people`. */
  people: string[];
  area_id: string | null;
  updated_at: string;
};

type AppVersion = {
  version: number;
  commit: string;
  message: string;
  author: string;
  at: number;
  live: boolean;
};

type AppDetail = {
  app: AppItem;
  versions: AppVersion[];
  /** The owner's only: what broke on the page or in the server. */
  errors: { version: number; message: string; source: string | null; at: number }[];
};

/** What opening an app gives the page: the iframe's ticket path, the app, and who's looking. */
export type Opened = { url: string; app: AppItem; me: { name: string } };

const appsKey = ["apps"] as const;
const appKey = (id: string) => ["app", id] as const;

/** The apps this person can see, the newest first; a 404 means apps aren't on here. */
export function useApps() {
  return useQuery({
    queryKey: appsKey,
    queryFn: async () => (await api<{ apps: AppItem[] }>("GET", "/api/apps")).apps,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
    staleTime: 10_000,
  });
}

/** Whether this Crew has apps at all: crew-apps is bound, so the list answers. */
export function useAppsOn(): boolean {
  const { error } = useApps();
  return !(error instanceof ApiError && error.status === 404);
}

/** One app, for its menu: its versions and, for its owner, its errors. */
export function useApp(id: string) {
  return useQuery({
    queryKey: appKey(id),
    queryFn: () => api<AppDetail>("GET", `/api/apps/${id}`),
    staleTime: 5_000,
  });
}

/** The owner's changes to an app; each refreshes the list and the app. */
function useAppChange<V>(id: string, run: (v: V) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => {
      void client.invalidateQueries({ queryKey: appsKey });
      void client.invalidateQueries({ queryKey: appKey(id) });
    },
  });
}

export function useShareApp(id: string) {
  return useAppChange(id, (v: { sharing: AppSharing; people: string[] }) =>
    api("PATCH", `/api/apps/${id}`, v),
  );
}

export function useMakeLive(id: string) {
  return useAppChange(id, (version: number) => api("POST", `/api/apps/${id}/live`, { version }));
}

export function useDeleteApp(id: string) {
  return useAppChange(id, () => api("DELETE", `/api/apps/${id}`));
}

/** "Yours · just you", "Alex's · shared with you", "Yours · with Priya": the list's line. */
export function whoseLine(app: AppItem, names: Map<string, string>): string {
  const whose = app.mine ? "Yours" : `${app.owner.name}'s`;
  if (app.sharing === "crew") return `${whose} · the whole crew`;
  if (!app.mine) return `${whose} · shared with you`;
  if (app.sharing === "owner") return `${whose} · just you`;
  const with_ = app.people.map((p) => names.get(p) ?? "someone").join(", ");
  return `${whose} · with ${with_}`;
}
